import {ValidatorInterface} from './ValidatorInterface';
import { AbstractValidator, ValidatorObject } from './AbstractValidator';
import { capitalize } from "@/functions/strings";
import {sprintf} from 'sprintf-js';
import Length from "../../../../source/js/shared/models/Geometry/Length";

/**
 * Validator that checks if the ratio between two dimensions meets the minimum.
 */
export class MinimumRatio extends AbstractValidator implements ValidatorInterface {
	ratios: Ratio[];
	declare value: number;

	/**
	 * Constructor.
	 *
	 * @param {ValidatorObject} options
	 */
	constructor( options: ValidatorObject ) {
		super( options );

		const ratios = this.instance.getRatios();

		if ( ! ratios.length ) {
			throw new Error( 'The MinimumRatio validator can only be used on shapes that have ratios.' );
		}

		const value = options.value ?? this.getDimensionValue();

		if ( typeof value !== 'number' ) {
			throw new Error( 'The MinimumValue validator can only be used on numbers.' );
		}

		this.value = value;
		this.ratios = ratios;
	}

	getInvalidRatios(): Ratio[] {
		const applicableRatios = this.ratios.filter( ratio => ratio.firstDimension === this.getDimension() );

		const invalidRatios = applicableRatios.filter( ratio => {
			const secondValue = this.instance[ `get${ ratio.secondDimension.split( '_' ).map( dimension_part => capitalize( dimension_part ) ).join( '' ) }` ]() ?? null;

			if ( ! ( secondValue instanceof Length ) ) {
				return false;
			}

			const calculatedRatio = this.value / secondValue.in_cm();

			return calculatedRatio < ratio.ratio;
		} );

		return invalidRatios;
	}

	/**
	 * @inheritDoc
	 */
	getMessage(): string {
		if ( super.getCustomMessage() ) {
			return super.getCustomMessage();
		}

		const invalidRatio = this.getInvalidRatios()[ 0 ];

		if ( ! invalidRatio ) {
			return '';
		}

		const { dimensions } = this.getValidatorTranslationStrings();

		if ( invalidRatio.firstDimension === this.getDimension() && invalidRatio.ratio === 0.5 ) {
			return sprintf( dimensions.ratio.half, dimensions.labels[ invalidRatio.firstDimension ], dimensions.labels[ invalidRatio.secondDimension ].toLowerCase() );
		}

		return sprintf( dimensions.ratio.min, dimensions.labels[ invalidRatio.firstDimension ].toLowerCase(), dimensions.labels[ invalidRatio.secondDimension ].toLowerCase(), invalidRatio.ratio );
	}

	/**
	 * @inheritDoc
	 */
	isValid(): boolean {
		return ! this.getInvalidRatios().length;
	}
}
